// C5 — Global leaderboard client. Fetches the rolling 7-day top-kill
// list from the `leaderboard` PartyKit party (room id `global`).
//
// The server-side handler lives in `party/leaderboard.ts`. Writes
// happen on match-end from each match DO; reads happen here when the
// user opens the leaderboard modal.
//
// Fallback: on timeout / error we synthesize a small mock list so the
// "GLOBAL" tab still renders something instead of a blank panel.

import { CONFIG } from '../config';

export type GlobalRow = {
  rank: number;
  name: string;
  role: 'ext' | 'int';
  kills: number;
  deaths: number;
  mode: string;
  arena: string;
  tsMs: number;
  mock?: boolean;
};

export type GlobalLeaderboard = {
  entries: GlobalRow[];
  genTs: number;
  windowDays: number;
  mock: boolean;
};

type ServerResponse = {
  entries: Array<Omit<GlobalRow, 'mock'>>;
  genTs: number;
  windowDays: number;
};

const TIMEOUT_MS = 2500;

let cached: { at: number; data: GlobalLeaderboard } | null = null;
const CACHE_TTL_MS = 20_000;

function mockEntries(): GlobalRow[] {
  const names = ['shadowfang', 'pixelwave', 'neonpanda', 'ghostbyte', 'luminari',
                 'vantablack', 'retroqueen', 'starburst', 'frostbyte', 'emberline'];
  const roles: ('ext' | 'int')[] = ['int','ext','ext','int','ext','int','int','ext','int','ext'];
  const modes = ['classic', 'tdm', 'race', 'pve'];
  const arenas = ['bedroom', 'gym', 'schoolyard', 'beach'];
  return names.map((n, i) => ({
    rank: i + 1,
    name: n,
    role: roles[i],
    kills: 42 - i * 3,
    deaths: 8 + i,
    mode: modes[i % 4],
    arena: arenas[i % 4],
    tsMs: Date.now() - i * 3600_000,
    mock: true,
  }));
}

/**
 * Fetch the current global leaderboard. Short client-side cache so
 * reopening the modal doesn't hammer the party. Returns `mock: true`
 * whenever the fetch failed or timed out — UI can show a "LIVE / MOCK"
 * chip based on this flag.
 */
export async function fetchGlobalLeaderboard(opts: { force?: boolean } = {}): Promise<GlobalLeaderboard> {
  if (!opts.force && cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.data;
  }

  const host = CONFIG.PARTY_HOST;
  const proto = /^localhost(:|$)|^127\./.test(host) ? 'http' : 'https';
  const url = `${proto}://${host}/parties/leaderboard/global`;

  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    const res = await fetch(url, { cache: 'no-store', signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) throw new Error(`bad status ${res.status}`);
    const data = (await res.json()) as ServerResponse;
    const entries: GlobalRow[] = Array.isArray(data.entries)
      ? data.entries.map((e) => ({ ...e, mock: false }))
      : [];
    const out: GlobalLeaderboard = {
      entries,
      genTs: typeof data.genTs === 'number' ? data.genTs : Date.now(),
      windowDays: typeof data.windowDays === 'number' ? data.windowDays : 7,
      mock: false,
    };
    cached = { at: Date.now(), data: out };
    return out;
  } catch {
    const out: GlobalLeaderboard = {
      entries: mockEntries(),
      genTs: Date.now(),
      windowDays: 7,
      mock: true,
    };
    // Cache the mock briefly too so a flapping network doesn't retry
    // on every open — the TTL means a real retry happens a few
    // seconds later anyway.
    cached = { at: Date.now(), data: out };
    return out;
  }
}
